import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { ContactService } from "../../services/contact.service";
import { S25EditableTextComponent } from "../s25-editable/s25-editable-text/s25.editable.text.component";

@TypeManagerDecorator("workflow-states")
@Component({
    selector: "workflow-states",
    template: `
        <div class="states-container">
            @if (selectedStage) {
                <div class="states-header">
                    <h3>States for {{ selectedStage.stageName }}</h3>
                    <button class="btn btn-primary" (click)="addState()">Add State</button>
                </div>

                <div class="states-list">
                    @for (state of selectedStage.states || []; track state.workflowStateId || state.id) {
                        <div class="state-item">
                            <div class="state-main">
                                <div class="state-info">
                                    <s25-ng-editable-text
                                        [val]="state.stateName"
                                        [hasCommit]="true"
                                        [hasCommitButton]="true"
                                        [hasCancelButton]="true"
                                        [customValidation]="getStateNameValidation(state)"
                                        [onCommit]="getStateNameCommitHandler(state)"
                                        placeholder="State name"
                                        class="state-name"
                                    ></s25-ng-editable-text>
                                    <div class="state-flags">
                                        <label class="flag">
                                            <input
                                                type="checkbox"
                                                [checked]="state.defaultEnabled !== false"
                                                (change)="updateStateDefaultEnabled(state, $event)"
                                            />
                                            Enabled
                                        </label>
                                    </div>
                                </div>
                                <div class="state-actions">
                                    <button class="btn btn-sm" (click)="toggleStateCollapse(state)">
                                        {{ isStateCollapsed(state) ? "▼" : "▲" }}
                                    </button>
                                    <button class="btn btn-sm btn-danger" (click)="deleteState(state, $event)">
                                        ×
                                    </button>
                                </div>
                            </div>

                            @if (!isStateCollapsed(state)) {
                                <div class="state-details">
                                    <div class="state-description">
                                        <textarea
                                            [(ngModel)]="state.description"
                                            (ngModelChange)="onStateChange()"
                                            placeholder="Description (optional)"
                                            rows="2"
                                        ></textarea>
                                    </div>

                                    <div class="contact-assignment">
                                        <h4>Contacts</h4>
                                        <div class="contact-mode">
                                            <label class="mode-option">
                                                <input
                                                    type="radio"
                                                    name="contactMode-{{ state.workflowStateId || state.id }}"
                                                    value="contacts"
                                                    [checked]="getContactAssignmentMode(state) === 'contacts'"
                                                    (change)="setContactAssignmentMode(state, 'contacts', false)"
                                                />
                                                Direct Contacts
                                            </label>
                                            <label class="mode-option">
                                                <input
                                                    type="radio"
                                                    name="contactMode-{{ state.workflowStateId || state.id }}"
                                                    value="approvalGroup"
                                                    [checked]="getContactAssignmentMode(state) === 'approvalGroup'"
                                                    (change)="setContactAssignmentMode(state, 'approvalGroup', false)"
                                                />
                                                Approval Group
                                            </label>
                                        </div>

                                        @if (getContactAssignmentMode(state) === "approvalGroup") {
                                            <div class="approval-group">
                                                <s25-approval-group-dropdown
                                                    [chosen]="getApprovalGroupForState(state)"
                                                    (chosenChange)="onApprovalGroupChange(state, $event)"
                                                    [noneItemLabel]="'No Approval Group'"
                                                ></s25-approval-group-dropdown>
                                            </div>
                                        } @else {
                                            <div class="contacts-list">
                                                @for (contact of state.contacts || []; track contact.contId) {
                                                    <div class="contact-item">
                                                        <span>{{
                                                            contact.contactName || "Contact " + contact.contId
                                                        }}</span>
                                                        <button
                                                            class="btn btn-sm btn-danger"
                                                            (click)="removeContact(state, contact)"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                }
                                                <s25-contact-dropdown
                                                    [chosen]="getContactDropdownSelection(state)"
                                                    (chosenChange)="onContactSelected(state, $event)"
                                                    [resetSelectedOnCleanup]="true"
                                                ></s25-contact-dropdown>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    }
                </div>
            } @else {
                <div class="no-stage">
                    <p>Select a stage to manage its states.</p>
                </div>
            }
        </div>
    `,
    styles: [
        `
            .states-container {
                padding: 20px;
            }

            .states-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 20px;
            }

            .states-header h3 {
                margin: 0;
                color: #333;
            }

            .btn {
                padding: 8px 16px;
                border: 1px solid #ddd;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                transition: all 0.2s;
            }

            .btn-primary {
                background: #007bff;
                color: white;
                border-color: #007bff;
            }

            .btn-primary:hover {
                background: #0056b3;
            }

            .btn-sm {
                padding: 4px 8px;
                font-size: 12px;
            }

            .btn-danger {
                background: #dc3545;
                color: white;
                border-color: #dc3545;
            }

            .btn-danger:hover {
                background: #c82333;
            }

            .states-list {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .state-item {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                overflow: hidden;
            }

            .state-main {
                padding: 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                background: #f8f9fa;
            }

            .state-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .state-name {
                font-weight: 500;
                color: #333;
            }

            .state-flags {
                display: flex;
                gap: 16px;
            }

            .flag {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 12px;
                color: #666;
                cursor: pointer;
            }

            .flag input {
                margin: 0;
            }

            .state-actions {
                display: flex;
                gap: 8px;
            }

            .state-details {
                padding: 20px;
                border-top: 1px solid #e0e0e0;
            }

            .state-description {
                margin-bottom: 20px;
            }

            .state-description textarea {
                width: 100%;
                padding: 8px 12px;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 14px;
                resize: vertical;
            }

            .contact-assignment {
                margin-bottom: 20px;
            }

            .contact-assignment h4 {
                margin: 0 0 12px 0;
                color: #333;
                font-size: 14px;
                font-weight: 500;
            }

            /* Fix dropdown positioning */
            :host ::ng-deep .ngDropdownDrop {
                position: relative !important;
            }

            .contact-mode {
                display: flex;
                gap: 16px;
                margin-bottom: 16px;
            }

            .mode-option {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 12px;
                color: #666;
                cursor: pointer;
            }

            .mode-option input {
                margin: 0;
            }

            .approval-group,
            .contacts-list {
                margin-top: 12px;
            }

            .contact-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 12px;
                background: #f8f9fa;
                border: 1px solid #e0e0e0;
                border-radius: 4px;
                margin-bottom: 8px;
            }

            .no-stage {
                text-align: center;
                padding: 40px 20px;
                color: #666;
            }
        `,
    ],
    standalone: true,
    imports: [CommonModule, FormsModule, S25DropdownPaginatedModule, S25EditableTextComponent],
    encapsulation: ViewEncapsulation.Emulated,
})
export class WorkflowStatesComponent implements OnInit {
    @Input() workflowProcess!: WorkflowTypes.WorkflowProcess;
    @Input() selectedStage: WorkflowTypes.WorkflowStage | null = null;
    @Output() statesChange = new EventEmitter<WorkflowTypes.WorkflowState[]>();

    private collapsedStates = new Set<number | string>();
    private selectedApprovalGroupsForStates = new Map<number | string, DropDownItem | null>();
    private contactAssignmentModes = new Map<number | string, "contacts" | "approvalGroup">();
    private contactDropdownSelections = new Map<number | string, DropDownItem | null>();
    private nextNegativeId = -1;

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        if (!this.selectedStage?.states) return;

        for (const state of this.selectedStage.states) {
            if (!state.contacts?.length) {
                this.setContactAssignmentMode(state, "approvalGroup", true);
            } else {
                this.setContactAssignmentMode(state, "contacts", true);
            }

            // Initialize approval group selection if state has an approval group ID
            if (state.approvalGroupId) {
                const stateKey = state.workflowStateId || state.id!;
                // Create a placeholder DropDownItem - the actual name will be loaded by the dropdown component
                this.selectedApprovalGroupsForStates.set(stateKey, {
                    itemId: state.approvalGroupId,
                    itemName: `Loading...`, // This will be replaced when the dropdown loads
                    itemTypeId: undefined,
                } as DropDownItem);
            }
        }

        const contIds = this.selectedStage.states.flatMap((s) => s.contacts?.map((c) => c.contId) || []);
        if (!contIds.length) return;

        try {
            const contactNames = await ContactService.getContactsNameAndFormal(contIds);
            const contactNameMap = new Map(contactNames.map((c) => [c.itemId, c.itemName]));
            this.selectedStage.states.forEach((s) => {
                s.contacts?.forEach((c) => (c.contactName = contactNameMap.get(c.contId) || `Contact ${c.contId}`));
            });
        } catch (error) {
            console.error("Error loading contact names:", error);
        }

        this.cd.detectChanges();
    }

    updateStateDefaultEnabled(state: WorkflowTypes.WorkflowState, event: Event) {
        const target = event.target as HTMLInputElement;
        state.defaultEnabled = target.checked;
        this.onStateChange();
    }

    onStateChange() {
        this.statesChange.emit(this.selectedStage?.states || []);
        this.cd.detectChanges();
    }

    addState() {
        if (!this.selectedStage) {
            console.error("No selected stage for adding state");
            return;
        }

        const nextId = this.getNextNegativeId();
        const newState: WorkflowTypes.WorkflowState = {
            id: nextId,
            workflowProcessId: this.workflowProcess.workflowProcessId,
            workflowStageId: this.selectedStage.workflowStageId,
            stateName: `State ${(this.selectedStage.states?.length || 0) + 1}`,
            description: "",
            defaultEnabled: true,
            contacts: [],
        };

        if (!this.selectedStage.states) {
            this.selectedStage.states = [];
        }

        this.selectedStage.states.push(newState);
        this.onStateChange();
    }

    private getNextNegativeId(): number {
        const allStates = this.selectedStage?.states || [];
        return allStates.reduce((minId, state) => Math.min(minId, state.id || -1), -1) - 1;
    }

    deleteState(state: WorkflowTypes.WorkflowState, event: Event) {
        event.stopPropagation();

        if (!this.selectedStage?.states) return;

        const index = this.selectedStage.states.findIndex(
            (s) => (s.workflowStateId && s.workflowStateId === state.workflowStateId) || (s.id && s.id === state.id),
        );

        if (index > -1) {
            this.selectedStage.states.splice(index, 1);
            this.onStateChange();
        }
    }

    toggleStateCollapse(state: WorkflowTypes.WorkflowState) {
        const stateKey = state.workflowStateId || state.id!;

        if (this.collapsedStates.has(stateKey)) {
            this.collapsedStates.delete(stateKey);
        } else {
            this.collapsedStates.add(stateKey);
        }
    }

    isStateCollapsed(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.collapsedStates.has(stateKey);
    }

    getStateNameValidation(state: WorkflowTypes.WorkflowState) {
        return (value: string) => {
            if (!value || value.trim().length === 0) {
                return ["State name is required"];
            }

            // Check for duplicate state names within the same stage
            const duplicateState = this.selectedStage?.states?.find(
                (s) =>
                    s.stateName === value.trim() &&
                    ((s.workflowStateId && s.workflowStateId !== state.workflowStateId) || (s.id && s.id !== state.id)),
            );

            if (duplicateState) {
                return ["State name must be unique within the stage"];
            }

            return true;
        };
    }

    getStateNameCommitHandler(state: WorkflowTypes.WorkflowState) {
        return (value: string) => {
            state.stateName = value;
            this.onStateChange();
        };
    }

    getApprovalGroupForState(state: WorkflowTypes.WorkflowState): DropDownItem | null {
        const stateKey = state.workflowStateId || state.id!;
        return this.selectedApprovalGroupsForStates.get(stateKey) || null;
    }

    onApprovalGroupChange(state: WorkflowTypes.WorkflowState, item: DropDownItem | null) {
        const stateKey = state.workflowStateId || state.id!;
        this.selectedApprovalGroupsForStates.set(stateKey, item);

        if (item && item.itemId) {
            state.approvalGroupId = parseInt(item.itemId.toString());
        } else {
            state.approvalGroupId = undefined;
        }

        this.onStateChange();
    }

    onContactSelected(state: WorkflowTypes.WorkflowState, selectedContact: DropDownItem) {
        if (!selectedContact) return;

        // Store the selection temporarily
        const stateKey = state.workflowStateId || state.id!;
        this.contactDropdownSelections.set(stateKey, selectedContact);

        // Check if contact is already added
        const existingContact = state.contacts?.find((c) => c.contId === Number(selectedContact.itemId));
        if (existingContact) {
            // Reset dropdown even if contact already exists using timeout bc we are currently in the same cycle that set the selection
            setTimeout(() => {
                this.contactDropdownSelections.set(stateKey, undefined);
                this.cd.detectChanges();
            }, 0);
            return;
        }

        // Add the new contact
        if (!state.contacts) {
            state.contacts = [];
        }

        state.contacts.push({
            contId: Number(selectedContact.itemId),
            contactName: selectedContact.itemName,
        });

        // Reset the dropdown selection using a timeout to ensure it happens after the current cycle because we are currently in the same cycle that set the selection
        setTimeout(() => {
            this.contactDropdownSelections.set(stateKey, undefined);
            this.cd.detectChanges();
        }, 0);

        this.onStateChange();
    }

    getContactDropdownSelection(state: WorkflowTypes.WorkflowState): DropDownItem | null {
        const stateKey = state.workflowStateId || state.id!;
        const selection = this.contactDropdownSelections.get(stateKey);
        return selection === undefined ? null : selection;
    }

    removeContact(state: WorkflowTypes.WorkflowState, contact: WorkflowTypes.WorkflowStateContact) {
        if (!state.contacts) return;

        const index = state.contacts.findIndex((c) => c.contId === contact.contId);
        if (index > -1) {
            state.contacts.splice(index, 1);
            this.onStateChange();
        }
    }

    getContactAssignmentMode(state: WorkflowTypes.WorkflowState): "contacts" | "approvalGroup" {
        const stateKey = state.workflowStateId || state.id!;
        const storedMode = this.contactAssignmentModes.get(stateKey);

        if (storedMode) {
            return storedMode;
        }

        // Determine mode based on existing data
        if (state.approvalGroupId) {
            this.contactAssignmentModes.set(stateKey, "approvalGroup");
            return "approvalGroup";
        } else {
            this.contactAssignmentModes.set(stateKey, "contacts");
            return "contacts";
        }
    }

    setContactAssignmentMode(
        state: WorkflowTypes.WorkflowState,
        mode: "contacts" | "approvalGroup",
        skipOnChange: boolean = false,
    ) {
        const stateKey = state.workflowStateId || state.id!;
        this.contactAssignmentModes.set(stateKey, mode);

        // Clear the other mode's data when switching
        if (mode === "contacts") {
            // Clear approval group data
            state.approvalGroupId = undefined;
            this.selectedApprovalGroupsForStates.set(stateKey, null);
        } else {
            // Clear contacts data
            state.contacts = [];
            this.contactDropdownSelections.set(stateKey, null);
        }

        if (!skipOnChange) {
            this.onStateChange();
        }
    }

    hasContactsAssigned(state: WorkflowTypes.WorkflowState): boolean {
        const mode = this.getContactAssignmentMode(state);
        if (mode === "approvalGroup") {
            const approvalGroup = this.getApprovalGroupForState(state);
            return approvalGroup !== null && approvalGroup.itemId !== null;
        } else {
            return (state.contacts && state.contacts.length > 0) || false;
        }
    }

    getContactCount(state: WorkflowTypes.WorkflowState): number {
        const mode = this.getContactAssignmentMode(state);
        if (mode === "approvalGroup") {
            const approvalGroup = this.getApprovalGroupForState(state);
            return approvalGroup && approvalGroup.itemId ? 1 : 0; // Approval groups count as 1 contact assignment
        } else {
            return state.contacts ? state.contacts.length : 0;
        }
    }
}
